import { useState } from 'react';
import { useParams } from 'react-router-dom';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import { Box, Button, Typography } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import { makeStyles } from '@mui/styles';
import { GridRowSelectionModel } from '@mui/x-data-grid/models';
import { saveAs } from 'file-saver';
import JSZip from 'jszip';
import { useDocument_DownloadFilesMutation } from 'queries';

import DocumentDeleteModal from 'pages/common/document/document-delete-modal';
import { NotificationSeverity, showAlert } from 'utils/notification-manager';
import useIsImpersonating from 'utils/useIsImpersonating';

import { DocumentTab } from './estate-documents-table';

const useStyles = makeStyles(() => ({
  container: {
    position: 'absolute',
    width: '100%',
    zIndex: 1,
    bottom: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  subContainer: {
    padding: '8px 24px',
    borderRadius: '16px',
    justifyContent: 'center',
    display: 'inline-flex',
    alignItems: 'center',
    border: '1px solid #ECEEF1',
    background: '#FFFFFF',
    boxShadow: '0px 20px 24px -4px rgba(0, 0, 0, 0.08), 0px 8px 8px -4px rgba(0, 0, 0, 0.03)',
  },
  title: {
    fontSize: '14px',
    fontWeight: 700,
  },
  selectAllButton: {
    marginLeft: '16px',
    border: '1px solid #ECEEF1',
    backgroundColor: '#F9F9FB',
    color: '#1C2541',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: 'none',
  },
  checkBox: { padding: 0, marginRight: '8px' },
  downloadButton: {
    marginLeft: '16px',
    border: '1px solid #ECEEF1',
    backgroundColor: '#F9F9FB',
    color: '#1C2541',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: 'none',
    minWidth: '32px',
    minHeight: '32px',
  },
  deleteButton: {
    marginLeft: '16px',
    border: '1px solid #1C2541',
    backgroundColor: '#1C2541',
    color: '#fff',
    borderRadius: '8px',
    fontSize: '14px',
    boxShadow: 'none',
  },
  deleteIcon: {
    marginRight: '8px',
  },
}));

type Props = {
  tab: DocumentTab;
  rowSelectionModel: GridRowSelectionModel;
  setRowSelectionModel: (value: GridRowSelectionModel) => void;
  documentIds: string[];
  onRefreshDocuments: () => void;
};

const FloatingMenu = ({
  rowSelectionModel,
  setRowSelectionModel,
  documentIds,
  onRefreshDocuments,
  tab,
}: Props) => {
  const classes = useStyles();
  const [isDownloading, setIsDownloading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const { portalId } = useParams<Record<string, string>>();
  const selectedIds = rowSelectionModel as string[];

  const isImpersonating = useIsImpersonating();

  const [downloadFiles] = useDocument_DownloadFilesMutation({
    variables: {
      ids: selectedIds,
      portalId,
    },
    onCompleted: async ({ data: downloadFilesData }) => {
      if (downloadFilesData.length > 1) {
        const time = new Date().getTime();
        const folderName = `trustate-vault-documents-${time}`;
        const zippedFolderName = `${folderName}.zip`;
        const zip = new JSZip();
        const folder = zip.folder(folderName)!;
        const fileNameCount: Record<string, number> = {};

        for (const i in downloadFilesData) {
          const downloadFile = downloadFilesData[i];
          let fileName = downloadFile.fileName;

          if (fileNameCount[fileName]) {
            fileNameCount[fileName] += 1;
            // Strip the extension and add count, then reattach the file extension
            fileName = `${fileName.replace(/\.[^/.]+$/, '')}(${
              fileNameCount[fileName]
              // matches the file extension (e.g., ".jpg") at the end of the filename
            })${downloadFile.fileName.match(/\.[^/.]+$/)}`;
          } else {
            fileNameCount[fileName] = 1;
          }

          const file: Blob =
            (await fetch(downloadFile.signedUrl)
              .then((res) => res.blob())
              .catch((e) => console.log('error fetching file: ', e))) || new Blob();
          folder.file(fileName, file);
        }

        await folder
          .generateAsync({ type: 'blob' }, (metadata) => {
            console.log(`zipping files progress: ${metadata.percent.toFixed(2)}%`);
            if (metadata.currentFile) {
              console.log(`zipping file: ${metadata.currentFile}`);
            }
          })
          .then((content) => {
            saveAs(content, zippedFolderName);
          });
      } else {
        window.open(downloadFilesData[0].signedUrl, '_blank');
      }
      setIsDownloading(false);
    },
    onError: (e) => {
      showAlert({
        message: 'Error downloading files: ' + e.message,
        severity: NotificationSeverity.ERROR,
      });
    },
  });
  const handleDownload = async () => {
    setIsDownloading(true);
    await downloadFiles();
  };
  const canDelete = tab === DocumentTab.Uploads || isImpersonating;

  return (
    <>
      <DocumentDeleteModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        documentIds={selectedIds}
        portalId={portalId}
        onDeleteSuccess={() => onRefreshDocuments()}
      />
      <Box className={classes.container}>
        <Box className={classes.subContainer}>
          <Typography className={classes.title}>{rowSelectionModel.length} selected</Typography>
          <Button
            component="span"
            variant="contained"
            size="small"
            className={classes.selectAllButton}
            onClick={() => setRowSelectionModel(documentIds)}
          >
            <Checkbox
              checked={rowSelectionModel.length === documentIds.length}
              size="small"
              className={classes.checkBox}
            />
            Select All
          </Button>
          <Button
            disabled={isDownloading}
            onClick={handleDownload}
            variant="contained"
            size="small"
            className={classes.downloadButton}
          >
            <DownloadIcon fontSize="small" />
          </Button>
          {canDelete && (
            <Button
              component="span"
              variant="contained"
              size="small"
              className={classes.deleteButton}
              onClick={() => setShowDeleteModal(true)}
            >
              <DeleteIcon fontSize="small" className={classes.deleteIcon} />
              Delete
            </Button>
          )}
        </Box>
      </Box>
    </>
  );
};

export default FloatingMenu;
